import React from 'react';
import { Database, BadgeCheck, Filter, Download, Search, Settings2, ShieldCheck, FileOutput } from 'lucide-react';

const MarketerFeatures: React.FC = () => {
  return (
    <section id="features" className="py-24 bg-slate-900 border-y border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* SOLUTION HEADER */}
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-6">Fresh Data. Verified Licenses. No Contracts.</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            We built the tool we wished we had. The only platform that combines professional license verification with enrichment.
          </p>
        </div>

        {/* FEATURES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-32">
          <div className="space-y-4">
            <div className="w-10 h-10 bg-sky-500/10 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-sky-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Verified Contact Info</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Direct phone numbers, emails, and business addresses — verified monthly, not quarterly. Stop chasing dead leads.
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
              <BadgeCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Real-Time Verification</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              We check state licensing boards daily. Know if someone is active, expired, or suspended before you reach out.
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
              <Filter className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Granular Filtering</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Search by state, zip code, specialty, years licensed, and more. Build targeted lists in minutes, not days.
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-10 h-10 bg-indigo-500/10 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Export Anywhere</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Download CSV files or connect via API. Works with any CRM or outreach tool you already use.
            </p>
          </div>
        </div>

        {/* HOW IT WORKS */}
        <div className="relative rounded-3xl bg-slate-950 p-8 md:p-12 border border-slate-800">
           <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 border border-slate-800 px-6 py-2 rounded-full text-sm font-bold text-white shadow-xl">
             HOW IT WORKS
           </div>

           <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-6">
             {/* Step 1 */}
             <div className="text-center relative">
               <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                 <Search className="w-6 h-6 text-sky-500" />
               </div>
               <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-800 -z-0"></div>
               <h4 className="text-white font-bold mb-2">1. Search</h4>
               <p className="text-xs text-slate-500">Enter your criteria: location, license type, specialty.</p>
             </div>

             {/* Step 2 */}
             <div className="text-center relative">
               <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                 <Settings2 className="w-6 h-6 text-sky-500" />
               </div>
               <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-800 -z-0"></div>
               <h4 className="text-white font-bold mb-2">2. Filter</h4>
               <p className="text-xs text-slate-500">Narrow down by status, brokerage, or market area.</p>
             </div>

             {/* Step 3 */}
             <div className="text-center relative">
               <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                 <ShieldCheck className="w-6 h-6 text-sky-500" />
               </div>
               <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-slate-800 -z-0"></div>
               <h4 className="text-white font-bold mb-2">3. Verify</h4>
               <p className="text-xs text-slate-500">See real-time license status for every record.</p>
             </div>

             {/* Step 4 */}
             <div className="text-center relative">
               <div className="w-16 h-16 mx-auto bg-slate-900 rounded-full flex items-center justify-center border border-slate-800 mb-6 shadow-lg relative z-10">
                 <FileOutput className="w-6 h-6 text-sky-500" />
               </div>
               <h4 className="text-white font-bold mb-2">4. Export</h4>
               <p className="text-xs text-slate-500">Download your list or push directly to your CRM.</p>
             </div>
           </div>
        </div>

      </div>
    </section>
  );
};

export default MarketerFeatures;